// src/components/widgets/SafetySignalLight.jsx
// 웹/데스크톱 전용 안전 신호등 컴포넌트
import React, { useEffect, useState, useMemo } from "react";
import { getSafetyDashboard } from "../../api/safety";

export default function SafetySignalLight({ onLightClick }) {
  const projectId = useMemo(() => localStorage.getItem("projectId"), []);
  const [data, setData] = useState({
    agg: { green: 0, yellow: 0, red: 0 },
    monthly: [],
  });

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res = await getSafetyDashboard({ project: projectId });
        setData(res || {});
      } catch (err) {
        console.error("안전 지표 불러오기 실패:", err);
      }
    })();
  }, [projectId]);

  const { green, yellow, red } = data.agg || {};
  const total = green + yellow + red;

  // 🔥 새로운 안전 지표 계산식
  // 1. 적합률 = (양호 + 조치중) / 전체 × 100% 
  //    → 주의(red)만 문제로 간주, 나머지는 모두 적합
  const complianceRate = total ? Math.round(((green + yellow) / total) * 100) : 0;

  // 2. 위험률 = 주의 / 전체 × 100%
  //    → 실제 위험한 항목의 비율
  const riskRate = total ? Math.round((red / total) * 100) : 0;

  // 3. 완료율 = 양호 / 전체 × 100%
  //    → 완전히 처리 완료된 항목의 비율
  const completionRate = total ? Math.round((green / total) * 100) : 0;

  const COLORS = {
    green: "#22c55e",
    yellow: "#eab308",
    red: "#ef4444",
  };

  const circles = [
    { label: "양호", value: green, color: COLORS.green, status: "green" },
    { label: "조치 중", value: yellow, color: COLORS.yellow, status: "yellow" },
    { label: "주의", value: red, color: COLORS.red, status: "red" },
  ];

  return (
    <div className="page-box">
      <h3 className="wgt-title">안전 점검 현황</h3>

      <div className="flex items-center justify-between gap-6">
        {/* 왼쪽: 신호등 */}
        <div className="flex flex-col items-center justify-center">
          <div className="flex gap-4 mb-3">
            {circles.map((c) => (
              <button
                key={c.label}
                onClick={() => onLightClick && onLightClick(c.status)}
                className="flex flex-col items-center justify-center w-20 h-20 rounded-full border-4 text-center transition-all hover:scale-105 active:scale-95 cursor-pointer"
                style={{ borderColor: c.color, color: c.color }}
              >
                <p className="text-xs font-semibold leading-tight">
                  {c.label}
                </p>
                <p className="text-lg font-bold mt-1">{c.value}</p>
              </button>
            ))}
          </div>
        </div>

        {/* 오른쪽: 새로운 안전 지표 */}
        <div className="flex-1 grid grid-cols-3 gap-4">
          {/* 적합률 (메인 지표) */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 text-center">
            <p className="text-xs text-blue-600 font-semibold mb-1">적합률</p>
            <p className="text-3xl font-bold text-blue-700 mb-1">{complianceRate}%</p>
            <p className="text-[10px] text-blue-500">
              양호 + 조치중
            </p>
          </div>

          {/* 위험률 */}
          <div className="bg-red-50 rounded-xl p-4 border border-red-200 text-center">
            <p className="text-xs text-red-600 font-semibold mb-1">위험률</p>
            <p className="text-3xl font-bold text-red-700 mb-1">{riskRate}%</p>
            <p className="text-[10px] text-red-500">
              주의 항목 비율
            </p>
          </div>

          {/* 완료율 */}
          <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
            <p className="text-xs text-green-600 font-semibold mb-1">완료율</p>
            <p className="text-3xl font-bold text-green-700 mb-1">{completionRate}%</p>
            <p className="text-[10px] text-green-500">
              양호 항목 비율
            </p>
          </div>
        </div>
      </div>

      {/* 하단: 총 건수 및 설명 */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-600">
            총 <span className="font-bold text-gray-900">{total}</span>건
            <span className="text-xs text-gray-500 ml-2">
              (양호 {green} · 조치중 {yellow} · 주의 {red})
            </span>
          </div>
          <div className="text-xs text-gray-500">
            💡 적합률 = 주의를 제외한 모든 항목 / 전체 × 100%
          </div>
        </div>
      </div>
    </div>
  );
}
